import React, { Fragment } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import { arrayOf, bool, func, node, oneOfType, shape, string } from 'prop-types';
import classNames from 'classnames';
import omit from 'lodash/omit';
import { find, pathOr } from 'ramda';

import { propTypes, LISTING_STATE_CLOSED } from '../../util/types';
import { parse, stringify } from '../../util/urlHelpers';
import { types as sdkTypes } from '../../util/sdkLoader';
import config from '../../config';
import { ModalInMobile, Button, SecondaryButton } from '../../components';
import { BookingDatesForm } from '../../forms';
import css from './BookingPanel.css';

const { Money } = sdkTypes;
// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;

const openBookModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), book: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeBookModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'book');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const openContactModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), contact: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeContactModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'contact');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const BookingPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    isOwnListing,
    unitType,
    onSubmit,
    authorDisplayName,
    onManageDisableScrolling,
    timeSlots,
    fetchTimeSlotsError,
    history,
    location,
    intl,
    showContactUser,
    onContactUser,
    maxCapacityToGuest,
    minCapacityToGuest,
    onSubmitEnquiry,
    suitableForChildren,
  } = props;

  const extras = pathOr([], ['attributes', 'publicData', 'paidExtrasData'], listing);
  const getExtraFee = (extras = [], extraKey) => {
    const extraFeeData = find(({ id } = {}) => id === extraKey, extras);
    const { amount, currency } = extraFeeData || {};
    return amount && currency && new Money(amount, currency);
  };
  const barbecueFee = getExtraFee(extras, 'barbecueFee');
  const towelsFee = getExtraFee(extras, 'towelsFee');
  const snackFee = getExtraFee(extras, 'snackFee');
  const jacuzziFee = getExtraFee(extras, 'jacuzziFee');
  const price = listing.attributes.price;
  const hasListingState = !!listing.attributes.state;
  const isClosed = hasListingState && listing.attributes.state === LISTING_STATE_CLOSED;
  const showBookingDatesForm = hasListingState && !isClosed;
  const showClosedListingHelpText = listing.id && isClosed;
  const isBook = !!parse(location.search).book;
  const isContact = !!parse(location.search).contact;

  const handleSubmit = values => {
    const selectedBarbecueFee = find(i => i === 'barbecueFee', pathOr([], ['additionalItems'], values)) && barbecueFee;
    const selectedTowelsFee = find(i => i === 'towelsFee', pathOr([], ['additionalItems'], values)) && towelsFee;
    const selectedSnackFee = find(i => i === 'snackFee', pathOr([], ['additionalItems'], values)) && snackFee;
    const selectedJacuzziFee = find(i => i === 'jacuzziFee', pathOr([], ['additionalItems'], values)) && jacuzziFee;
    onSubmit({
      ...values,
      barbecueFee: selectedBarbecueFee,
      towelsFee: selectedTowelsFee,
      snackFee: selectedSnackFee,
      jacuzziFee: selectedJacuzziFee,
    });
  };

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'BookingPanel.subTitleClosedListing' })
    : null;

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.bookingTitle);

  const title = intl.formatMessage({ id: 'BookingPanel.title' });
  const contactTitle = intl.formatMessage({ id: 'BookingPanel.contactTitle' });
  const contactUserButton = (!isBook && showContactUser) && (
    <SecondaryButton onClick={() => openContactModal(isOwnListing, isClosed, history, location)}>
      <FormattedMessage id="ListingPage.contactUser"/>
    </SecondaryButton>
  );

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="BookingDatesFormInModal"
        isModalOpenOnMobile={isBook || isContact}
        onClose={() => isBook ? closeBookModal(history, location) : closeContactModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.modalHeading}>
          <h1 className={css.title}>{isContact ? contactTitle : title}</h1>
        </div>

        <div className={css.bookingHeading}>
          <h2 className={titleClasses}>{title}</h2>
          {subTitleText ? <div className={css.bookingHelp}>{subTitleText}</div> : null}
        </div>
        {showBookingDatesForm ? (
          <BookingDatesForm
            className={css.bookingForm}
            formId="BookingPanel"
            submitButtonWrapperClassName={css.bookingDatesSubmitButtonWrapper}
            unitType={unitType}
            onSubmit={handleSubmit}
            price={price}
            listing={listing}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            timeSlots={timeSlots}
            fetchTimeSlotsError={fetchTimeSlotsError}
            maxCapacityToGuest={maxCapacityToGuest}
            minCapacityToGuest={minCapacityToGuest}
            isBook={isBook}
            showContactUser={showContactUser}
            onContactUser={onContactUser}
            isOnlyContact={isContact}
            authorDisplayName={authorDisplayName}
            onSubmitEnquiry={onSubmitEnquiry}
            barbecueFee={barbecueFee}
            towelsFee={towelsFee}
            snackFee={snackFee}
            jacuzziFee={jacuzziFee}
            suitableForChildren={suitableForChildren}
          />
        ) : null}
      </ModalInMobile>
      <div className={css.openBookingForm}>
        {showBookingDatesForm ? (
          <Fragment>
            {contactUserButton}
            <Button
              rootClassName={css.bookButton}
              onClick={() => openBookModal(isOwnListing, isClosed, history, location)}
            >
              <FormattedMessage id="BookingPanel.ctaButtonMessage" />
            </Button>
          </Fragment>
        ) : isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="BookingPanel.closedListingButtonText" />
          </div>
        ) : null}
      </div>
    </div>
  );
};

BookingPanel.defaultProps = {
  rootClassName: null,
  className: null,
  isOwnListing: false,
  unitType: config.bookingUnitType,
  timeSlots: null,
  fetchTimeSlotsError: null,
};

BookingPanel.propTypes = {
  rootClassName: string,
  className: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  isOwnListing: bool,
  unitType: propTypes.bookingUnitType,
  onSubmit: func.isRequired,
  authorDisplayName: oneOfType([node, string]).isRequired,
  onManageDisableScrolling: func.isRequired,
  timeSlots: arrayOf(propTypes.timeSlot),
  fetchTimeSlotsError: propTypes.error,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(
  withRouter,
  injectIntl
)(BookingPanel);



// WEBPACK FOOTER //
// ./src/components/BookingPanel/BookingPanel.js